import React from 'react';
import { User, CreditCard, Shield, X } from 'lucide-react';
import { useAccountSettings, SettingsSection } from '@/context/AccountSettingsContext';
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarGroupLabel,
  useSidebar
} from '@/components/ui/sidebar';
import { cn } from '@/lib/core/utils';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

type NavItem = {
  id: SettingsSection;
  label: string;
  icon: React.ElementType;
  tooltip?: string;
};

const accountSettingsNavigation: NavItem[] = [
  {
    id: 'profile',
    label: 'Profile',
    icon: User,
    tooltip: 'Manage your profile'
  },
  {
    id: 'plan',
    label: 'Plan & Usage',
    icon: CreditCard,
    tooltip: 'View plan and usage'
  },
  {
    id: 'security',
    label: 'Security',
    icon: Shield,
    tooltip: 'Security settings'
  }
];

export const AccountSettingsSidebarNav = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  const { activeSection, setActiveSection, closeAccountSettings } = useAccountSettings();
  const { isMobile, setOpenMobile } = useSidebar();
  const { isEmployee } = useAuth();

  const handleItemClick = (item: NavItem) => {
    setActiveSection(item.id);
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  const handleClose = () => {
    closeAccountSettings();
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Filter out plan section for employees
  const filteredNavigation = accountSettingsNavigation.filter(
    (item) => !(isEmployee && item.id === 'plan')
  );

  return (
    <div
      ref={ref}
      data-sidebar="navigation"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    >
      <SidebarGroup>
        <SidebarGroupLabel className="flex items-center justify-between pr-2">
          <span>Account Settings</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            onClick={handleClose}
          >
            <X className="h-3 w-3" />
            <span className="sr-only">Close settings</span>
          </Button>
        </SidebarGroupLabel>

        <SidebarMenu>
          {filteredNavigation.map((item) => (
            <SidebarMenuItem key={item.id}>
              <SidebarMenuButton
                isActive={activeSection === item.id}
                tooltip={item.tooltip || item.label}
                onClick={() => handleItemClick(item)}
              >
                <item.icon />
                <span>{item.label}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    </div>
  );
});

AccountSettingsSidebarNav.displayName = 'AccountSettingsSidebarNav';
